import { Text } from '@mantine/core';
import { RISK_SEVIYELERI, RISK_SEVIYESI_ETIKETI, type RiskSeviyesi } from '@isg/validation';
import { OlcekRozeti, Rozet } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * Risk yüzeyinin paylaşılan parçaları (F3B·S9 — Kapsam §16.12).
 *
 * Kademe ETİKETLERİ burada durur ve **i18n katmanının risk kolu**dur
 * (Teknoloji §10: enum'a Türkçe metin girmez — token İngilizce, etiket
 * ekranda çevrilir). Dört kademe **§19/5'te KİLİTLİDİR**; beşinci bir rozet
 * eklemek ürün kararıdır.
 */
/**
 * ✏️ **F4·S9: ETİKET HARİTASI `@isg/validation`A TAŞINDI.** Aynı metni PDF
 * çıktısı da basıyor (çıktı sunucuda üretilir); iki kopya tutulsaydı ekran
 * ile denetime giden belge farklı kelime kullanabilirdi.
 */
const SEVIYE_ETIKETI = RISK_SEVIYESI_ETIKETI;

/**
 * Bant → **ÖLÇEK KADEMESİ** (P8 — madde 107'nin kod karşılığı; görsel dil turu).
 *
 * ✏️ **REVİZE (görsel dil turu): bant artık `Rozet`in beş anlamından ÇIKTI.**
 * ~~`RISK_YUKSEK_SEVIYELER` → `engel` · `medium` → `dikkat` · kalan →
 * `olumlu`~~ eşlemesi **beş anlamın dört bandı ayıramaması** yüzünden `high`
 * ile `unacceptable`ı aynı renge düşürüyordu. Artık bant, `OlcekRozeti`nin
 * **kendi dört kademeli dizisinden** çizilir (yeşil → sarı → turuncu →
 * kırmızı) ve iki üst bant **AYRI** görünür: sahada *"yüksek"* önlem
 * gerektirir, *"kabul edilemez"* iş yapılmadan önce indirilmelidir.
 *
 * ⚠️ **KADEME SIRASI `RISK_SEVIYELERI`DİR, İKİNCİ BİR SIRA YAZILMAZ** — dizinin
 * uzunluğu da oradan gelir, yani metodoloji beş bant getirdiği gün ölçek
 * dizisi `theme.ts`te büyür, bu dosya değişmez.
 *
 * ⚠️ **P3-a AYNEN GEÇERLİ:** en üst kademe kırmızıdır — risk bandı hiçbir kapıyı
 * kapatmadığı hâlde (R8 kanaryası). *İSG dilinde kırmızı riskin AĞIRLIĞIDIR,
 * sistemin kapısı değil.* `RISK_YUKSEK_SEVIYELER` R5'in **KAPI** kümesi olarak
 * yerinde durur; ölçek onu renk eşlemesinden ayırır, kapıdan değil.
 */

/**
 * Bant rozeti. **Seviye NULL olabilir** ve bu bir hata değil, D8 deseninin
 * kendisidir: bant tablosu tanımlı değilse puan hesaplanır ama seviye
 * belirlenemez — ekran bunu SUSARAK değil, açıkça söyler. ⚠️ NULL bir kademe
 * DEĞİLDİR (ölçeğin dışındadır) — bu yüzden ölçek rozetiyle değil, "bilgi"
 * anlamıyla çizilir: *bilgi bir durum değil, bir etikettir.*
 *
 * ✏️ **MADDE 220 / R10 (11.09.2026): ROZET YALNIZ BANDI SÖYLER, SKORU
 * SÖYLEMEZ.** Eskiden *"15 · Yüksek"* tek hücrede basılıyordu; skor artık
 * **kendi hücresindedir** (madde tablosunun *Değer* kolonu). `hamSkor` hâlâ
 * alınır ama yalnız *"puanlanmamış"* ile *"puanlı ama bandı yok"* ayrımı
 * için — metne basılmaz.
 *
 * ✏️ **İKİ SKOR (R1):** aynı bileşen mevcut ve hedef bandı çizer; `testOn`
 * hangisi olduğunu test kimliğine yazar (`risk-seviye-*` · `risk-hedef-seviye-*`).
 */
export function SeviyeRozeti({
  seviye,
  hamSkor,
  testOn = 'risk-seviye',
}: {
  seviye: RiskSeviyesi | null;
  hamSkor: number | null;
  testOn?: string;
}): React.JSX.Element {
  if (seviye === null) {
    if (hamSkor === null) {
      return (
        <Text size="sm" c="dimmed" data-testid={`${testOn}-puansiz`}>
          —
        </Text>
      );
    }
    return (
      <Rozet anlam="bilgi" testId={`${testOn}-yok`}>
        bant yok
      </Rozet>
    );
  }
  return (
    <OlcekRozeti
      kademe={RISK_SEVIYELERI.indexOf(seviye)}
      kademeSayisi={RISK_SEVIYELERI.length}
      testId={`${testOn}-${seviye}`}
    >
      {SEVIYE_ETIKETI[seviye]}
    </OlcekRozeti>
  );
}

/** Kapsam tipi etiketi (R2 — iki yaklaşım da desteklenir). */
export function KapsamMetni({
  kapsamTipi,
  locationAd,
  faaliyetAdi,
}: {
  kapsamTipi: 'lokasyon' | 'faaliyet';
  locationAd: string | null;
  faaliyetAdi: string | null;
}): React.JSX.Element {
  return (
    <Text size="sm">
      {kapsamTipi === 'lokasyon'
        ? `Lokasyon: ${locationAd ?? '—'}`
        : `Faaliyet: ${faaliyetAdi ?? '—'}`}
    </Text>
  );
}

/**
 * İKİ HEDEF SORGUNUN ORTAK PANELİ (§16.12/12) — olay detayı ve ekipman
 * kartı AYNI bileşeni kullanır. **Bulunamama cümlesi SUNUCUDAN gelir**
 * (tek dil; ekran metin uydurmaz).
 */
export function RiskEslesmePaneli({
  baslik,
  veri,
  testId,
}: {
  baslik: string;
  veri:
    | {
        bulundu: boolean;
        mesaj: string;
        maddeler: {
          id: string;
          assessmentAd: string;
          faaliyet: string;
          tehlike: string;
          /** Kısmi madde (§16.12/16-j): riski seçilmemiş olabilir. */
          risk: string | null;
          mevcutOnlem: string | null;
          riskSeviyesi: RiskSeviyesi | null;
          eslesmeEkseni: ('lokasyon' | 'mekanizma' | 'ekipman')[];
        }[];
      }
    | undefined;
  testId: string;
}): React.JSX.Element | null {
  if (!veri) return null;
  return (
    <div data-testid={testId}>
      <Text fw={600} size="sm" mb={4}>
        {baslik}
      </Text>
      {!veri.bulundu ? (
        <Text size="sm" c={ANLAM.dikkat} data-testid={`${testId}-bulunamadi`}>
          {veri.mesaj}
        </Text>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb={4}>
            {veri.mesaj}
          </Text>
          {veri.maddeler.map((m) => (
            // `div`: satır bir rozet (Badge = div) taşır — `<p>` içinde `<div>`
            // geçersiz DOM'dur ve tarayıcı konsolunda hata üretiyordu.
            <Text key={m.id} component="div" size="sm" data-testid={`${testId}-madde`}>
              <b>{m.assessmentAd}</b> · {m.faaliyet} — {m.tehlike} → {m.risk ?? '—'}{' '}
              {/* R2: eşleşme panelleri MEVCUT bandı gösterir. */}
              <SeviyeRozeti seviye={m.riskSeviyesi} hamSkor={null} />{' '}
              {m.eslesmeEkseni.map((e) => (
                <Rozet key={e} anlam="bilgi">
                  {e}
                </Rozet>
              ))}
              {m.mevcutOnlem ? ` · Mevcut önlem: ${m.mevcutOnlem}` : ''}
            </Text>
          ))}
        </>
      )}
    </div>
  );
}
